import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import './saved.css';
import {
  BottomNav,
  BUYER_NAV,
  ColorRail,
  LockIcon,
  MessageIcon,
  TopBar,
} from '../../../commodity/components/caria';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';
import { fetchMyContacts, MyContact } from '../api/contact.api';

/**
 * MyContacts (T0 Epic B.6) — the buyer's contact list and re-entry point.
 * Reads GET /contacts/mine (authenticated).
 *
 * Ported onto the CAR-IA chrome and the prototype's row-card language, sharing
 * saved.css with /saved — the two surfaces are the same shape. It previously
 * rendered inside the pre-lime forest-green MainLayout (sidebar, English
 * footer), which below 768px opened a full-viewport drawer over the content.
 *
 * GET /contacts/mine is `@Roles(BUYER)`, so a seller gets a 403; that is
 * correct authorization, and it now renders as a Spanish explanatory state
 * rather than the raw API string.
 */

type State =
  | { kind: 'loading' }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; rows: MyContact[] };

const isForbidden = (err: unknown): boolean => {
  const msg = err instanceof Error ? err.message : String(err ?? '');
  return /insufficient permissions|forbidden|\b403\b/i.test(msg);
};

const rd = (n: number) => `RD$ ${n.toLocaleString('es-DO')}`;
const day = (iso: string) => new Date(iso).toLocaleDateString('es-DO');

/** WHATSAPP → WhatsApp, so the channel does not shout in the row. */
const channelLabel = (c: string) => c.charAt(0) + c.slice(1).toLowerCase();

export const MyContacts: React.FC = () => {
  const navigate = useNavigate();
  const authChrome = useAuthChrome();
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let active = true;
    fetchMyContacts()
      .then((rows) => active && setState({ kind: 'ready', rows }))
      .catch((err) => {
        if (!active) return;
        setState(
          isForbidden(err)
            ? { kind: 'forbidden' }
            : {
                kind: 'error',
                message:
                  err instanceof Error ? err.message : 'No se pudieron cargar tus contactos.',
              },
        );
      });
    return () => {
      active = false;
    };
  }, []);

  const rows = state.kind === 'ready' ? state.rows : [];

  return (
    <div className="cia-saved">
      <TopBar {...authChrome} nav={BUYER_NAV} />
      <ColorRail />

      <div className="cia-saved-wrap">
        <div className="cia-saved-head">
          <h1>Mis contactos</h1>
          {state.kind === 'ready' && rows.length > 0 && (
            <span className="cia-saved-count">{rows.length}</span>
          )}
        </div>
        <p className="cia-saved-sub">Los vendedores con los que ya hablaste.</p>

        {state.kind === 'loading' && (
          <div className="cia-saved-state" data-testid="my-contacts-loading" aria-busy="true">
            <p>Cargando…</p>
          </div>
        )}

        {state.kind === 'forbidden' && (
          <div className="cia-saved-state" data-testid="my-contacts-forbidden">
            <span className="cia-saved-state-ic">
              <LockIcon size={25} strokeWidth={1.8} />
            </span>
            <h2>Mis contactos es una función de comprador</h2>
            <p>
              Tu cuenta está registrada como vendedor. Los contactos que recibes aparecen en tu
              panel de vendedor.
            </p>
            <button className="cia-saved-cta" onClick={() => navigate('/my-listings')}>
              Ir a mis anuncios
            </button>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="cia-saved-state" data-testid="my-contacts-error">
            <h2>No pudimos cargar tus contactos</h2>
            <p role="alert">{state.message}</p>
            <button className="cia-saved-cta" onClick={() => navigate('/search')}>
              Explorar el catálogo
            </button>
          </div>
        )}

        {state.kind === 'ready' && rows.length === 0 && (
          <div className="cia-saved-state" data-testid="my-contacts-empty">
            <span className="cia-saved-state-ic">
              <MessageIcon size={25} strokeWidth={1.8} />
            </span>
            <h2>Todavía no has contactado a nadie</h2>
            <p>Cuando escribas a un vendedor, la conversación quedará aquí.</p>
            <button className="cia-saved-cta" onClick={() => navigate('/search')}>
              Explorar el catálogo
            </button>
          </div>
        )}

        {state.kind === 'ready' && rows.length > 0 && (
          <div className="cia-saved-list" data-testid="my-contacts">
            {rows.map(({ contact, vehicle }) => (
              <div key={contact.contactId} className="cia-saved-row">
                <span className="cia-saved-info">
                  <button
                    className="cia-saved-title"
                    onClick={() => navigate(`/vehicles/${contact.vehicleId}`)}
                  >
                    {vehicle?.title ?? 'Vehículo'}
                  </button>
                  <span className="cia-saved-meta">
                    {channelLabel(contact.channel)} · {day(contact.createdAt)}
                  </span>
                </span>
                {vehicle?.listingPrice != null && (
                  <span className="cia-saved-price">{rd(vehicle.listingPrice)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default MyContacts;
